import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Loader2, UserPlus, UserMinus } from 'lucide-react'
import { socialApi } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

interface FollowButtonProps {
  userId: string
  isFollowing?: boolean
  username?: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  showIcon?: boolean
  onFollowChange?: (isFollowing: boolean) => void
}

export function FollowButton({
  userId,
  isFollowing: initialIsFollowing = false,
  username,
  variant = 'default',
  size = 'default',
  showIcon = true,
  onFollowChange,
}: FollowButtonProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing)

  const followMutation = useMutation({
    mutationFn: () => socialApi.follow(userId),
    onMutate: async () => {
      // Optimistically update
      setIsFollowing(true)
      onFollowChange?.(true)

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['profile', username] })

      // Snapshot previous value
      const previousData = queryClient.getQueryData(['profile', username])

      // Optimistically update to the new value
      queryClient.setQueryData(['profile', username], (old: any) => {
        if (!old?.data?.profile) return old
        return {
          ...old,
          data: {
            ...old.data,
            profile: {
              ...old.data.profile,
              followers_count: (old.data.profile.followers_count || 0) + 1,
            },
            is_following: true,
          },
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      setIsFollowing(false)
      onFollowChange?.(false)
      if (context?.previousData) {
        queryClient.setQueryData(['profile', username], context.previousData)
      }
    },
    onSettled: () => {
      // Refetch to ensure we're in sync
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['profile', username] })
      }
    },
  })

  const unfollowMutation = useMutation({
    mutationFn: () => socialApi.unfollow(userId),
    onMutate: async () => {
      // Optimistically update
      setIsFollowing(false)
      onFollowChange?.(false)

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['profile', username] })

      // Snapshot previous value
      const previousData = queryClient.getQueryData(['profile', username])

      // Optimistically update to the new value
      queryClient.setQueryData(['profile', username], (old: any) => {
        if (!old?.data?.profile) return old
        return {
          ...old,
          data: {
            ...old.data,
            profile: {
              ...old.data.profile,
              followers_count: Math.max((old.data.profile.followers_count || 0) - 1, 0),
            },
            is_following: false,
          },
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      setIsFollowing(true)
      onFollowChange?.(true)
      if (context?.previousData) {
        queryClient.setQueryData(['profile', username], context.previousData)
      }
    },
    onSettled: () => {
      // Refetch to ensure we're in sync
      if (username) {
        queryClient.invalidateQueries({ queryKey: ['profile', username] })
      }
    },
  })

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!user) {
      window.location.href = '/login'
      return
    }

    if (isFollowing) {
      unfollowMutation.mutate()
    } else {
      followMutation.mutate()
    }
  }

  const isPending = followMutation.isPending || unfollowMutation.isPending

  // Don't show button for own profile
  if (user?.id === userId) {
    return null
  }

  return (
    <Button
      variant={isFollowing ? 'outline' : variant}
      size={size}
      onClick={handleClick}
      disabled={isPending}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          {showIcon && (
            isFollowing ? (
              <UserMinus className="mr-2 h-4 w-4" />
            ) : (
              <UserPlus className="mr-2 h-4 w-4" />
            )
          )}
          {isFollowing ? 'Following' : 'Follow'}
        </>
      )}
    </Button>
  )
}
